import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  ArrowUpRight,
  ChevronRight,
  Code2,
  Palette,
  Smartphone,
  Database,
  GitBranch,
  Zap,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import Hero3D from "./Hero3D";
import TiltCard from "./TiltCard";

/* ----------------------------------------------------------------------- */
/* SCRAMBLE — layout-stable text-scramble that lands on `target`.          */
/* Locks width, keeps spaces/punctuation, draws from ALNUM pool.            */
const SC_POOL = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const escHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function runScramble(el, target, dur = 900) {
  if (!el) return;
  if (!el.dataset.lockedWidth) {
    const w = el.getBoundingClientRect().width;
    if (w) {
      el.style.minWidth = `${w}px`;
      el.style.display = "inline-block";
    }
    el.dataset.lockedWidth = "1";
  }
  const chars = Array.from(target);
  const q = chars.map((ch, i) => {
    const sp = /\s/.test(ch);
    const pn = /[^A-Za-z0-9]/.test(ch);
    return {
      to: ch,
      reveal: sp ? 0 : (i / chars.length) * dur * 0.55 + Math.random() * dur * 0.45,
      keep: sp || pn,
      cur: "",
    };
  });
  const t0 = performance.now();
  function frame(now) {
    const e = now - t0;
    let out = "",
      done = 0;
    for (const s of q) {
      if (s.keep || e >= s.reveal) {
        out += escHtml(s.to === " " ? " " : s.to);
        done++;
      } else {
        if (!s.cur || Math.random() < 0.5)
          s.cur = SC_POOL[Math.floor(Math.random() * SC_POOL.length)];
        out += `<span style="color:var(--chapter-accent);opacity:.85">${s.cur}</span>`;
      }
    }
    el.innerHTML = out;
    if (done < q.length) requestAnimationFrame(frame);
    else el.textContent = target;
  }
  requestAnimationFrame(frame);
}

/* Scramble the element when it first scrolls into view. */
function ScrambleText({ text, as: As = "span", className, style }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (reduceMotion || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !el.dataset.scrambled) {
            el.dataset.scrambled = "1";
            runScramble(el, text, 1100);
          }
        });
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [text, reduceMotion]);
  return (
    <As ref={ref} className={className} style={style}>
      {text}
    </As>
  );
}

/* Count from 0 to a target number once visible. */
function Counter({ to = 0, suffix = "", duration = 1400, className }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    if (reduceMotion) {
      el.textContent = `${to}${suffix}`;
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !el.dataset.done) {
            el.dataset.done = "1";
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              el.textContent = `${Math.round(eased * to)}${suffix}`;
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.6 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, suffix, duration, reduceMotion]);
  return (
    <span ref={ref} className={className}>
      0{suffix}
    </span>
  );
}

/* Attach mousemove magnet pull + card-spot tracking to children. */
function useMagnetAndSpotEffects(rootRef, reduceMotion) {
  useEffect(() => {
    if (reduceMotion || !rootRef.current) return;
    const root = rootRef.current;

    const magnets = root.querySelectorAll(".kx-magnet");
    const magnetCleanup = [];
    magnets.forEach((el) => {
      let raf;
      const onMove = (e) => {
        const r = el.getBoundingClientRect();
        const px = e.clientX - (r.left + r.width / 2);
        const py = e.clientY - (r.top + r.height / 2);
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.style.transform = `translate3d(${Math.max(-12, Math.min(12, px * 0.25))}px,${Math.max(
            -12,
            Math.min(12, py * 0.25),
          )}px,0)`;
        });
      };
      const onLeave = () => {
        cancelAnimationFrame(raf);
        el.style.transform = "translate3d(0,0,0)";
      };
      el.addEventListener("mousemove", onMove);
      el.addEventListener("mouseleave", onLeave);
      magnetCleanup.push(() => {
        el.removeEventListener("mousemove", onMove);
        el.removeEventListener("mouseleave", onLeave);
      });
    });

    const cards = root.querySelectorAll(".kx-stat,.kx-reel-panel,.kx-channel");
    const cardCleanup = [];
    cards.forEach((card) => {
      const onMove = (e) => {
        const r = card.getBoundingClientRect();
        card.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
        card.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
      };
      card.addEventListener("mousemove", onMove);
      cardCleanup.push(() => card.removeEventListener("mousemove", onMove));
    });

    return () => {
      magnetCleanup.forEach((fn) => fn());
      cardCleanup.forEach((fn) => fn());
    };
  }, [rootRef, reduceMotion]);
}

const SECTIONS = [
  { id: "home",     label: "INTRO",    num: "00", accent: "#82b1f7", accentRgb: "130, 177, 247" },
  { id: "about",    label: "PROFILE",  num: "01", accent: "#65e0a4", accentRgb: "101, 224, 164" },
  { id: "skills",   label: "SKILLS",   num: "02", accent: "#ffd454", accentRgb: "255, 212,  84" },
  { id: "services", label: "SERVICES", num: "03", accent: "#ff9bb7", accentRgb: "255, 155, 183" },
  { id: "contact",  label: "CONNECT",  num: "04", accent: "#c4a8ff", accentRgb: "196, 168, 255" },
];

/* ---------- Letter-by-letter stagger reveal helpers --------------------- */
const splitChars = (text) => Array.from(text);

/* One observer on the parent <h2> drives a stagger over the children. Per-
   character whileInView is brittle on tiny inline-block spans and could leave
   individual chars stuck at opacity: 0 (taking layout space, invisible). */
const headingContainerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.045 } },
};
const headingCharVariants = {
  hidden: { opacity: 0, y: 24, filter: "blur(4px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.55, ease: [0.2, 0.7, 0.2, 1] },
  },
};

const StaggeredHeading = ({ text, className, delay = 0 }) => (
  <motion.h2
    className={className}
    aria-label={text}
    variants={headingContainerVariants}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-10%" }}
    transition={{ delayChildren: delay }}
  >
    {splitChars(text).map((c, i) => (
      <motion.span
        key={i}
        className="kx-stagger-char"
        variants={headingCharVariants}
        aria-hidden
      >
        {c === " " ? " " : c}
      </motion.span>
    ))}
  </motion.h2>
);

const SKILLS = [
  { name: "React", level: 95, icon: Code2 },
  { name: "JavaScript", level: 90, icon: Zap },
  { name: "Angular", level: 85, icon: GitBranch },
  { name: "TypeScript", level: 80, icon: Database },
  { name: "UI / UX", level: 75, icon: Palette },
  { name: "Tailwind", level: 85, icon: Smartphone },
];

const SERVICES = [
  {
    no: "01",
    title: "Frontend Engineering",
    description:
      "Production-grade interfaces with React, Angular, and a strong eye for motion and detail.",
  },
  {
    no: "02",
    title: "UI Implementation",
    description:
      "Pixel-perfect translation of design systems into accessible, performant code.",
  },
  {
    no: "03",
    title: "Component Libraries",
    description:
      "Scalable, reusable systems with first-class typing, theming, and tests.",
  },
];

/* ======================================================================== */
/* SCROLL-LINKED CHAPTERS                                                   */
/* Each chapter maps its own choreography 1:1 onto scroll position, so it   */
/* plays forward and backward under the thumb, and each one moves in a     */
/* different register so the 3D device stays the constant while the UI     */
/* changes character around it:                                            */
/*   00 HOME     - copy parts to both edges, camera-push on the name        */
/*   01 PROFILE  - dossier wiped out of the left edge, stats flip open      */
/*   02 SKILLS   - chips converge from a radial scatter (device explosion)  */
/*   03 SERVICES - panel hinges in from the right edge, rows ledger-slide   */
/*   04 CONNECT  - stage rises from the floor, cards stand up               */
/* ======================================================================== */

/* p = 0 when the section top meets the viewport bottom, 1 when the section
   bottom leaves the viewport top. 0.5 = section centred in the viewport. */
const useChapterProgress = (ref) =>
  useScroll({ target: ref, offset: ["start end", "end start"] }).scrollYProgress;

/* Enter over [a, b], hold, then leave over EXIT: from -> to -> to -> back.
   ponytail: exits are fixed to p, not to section height, so a chapter much
   taller than the viewport starts leaving while its tail is still readable.
   Upgrade path: derive EXIT from section/viewport ratio in useChapterProgress. */
const EXIT = [0.68, 0.95];
const useInOut = (p, [a, b], from, to, back = from) =>
  useTransform(p, [a, b, EXIT[0], EXIT[1]], [from, to, to, back]);

/* Terminal line that types itself the first time it scrolls into view.
   Renders the full text first so the row is laid out at final width, then
   locks that width and clears before typing (no layout shift). */
function TypeLine({ text, delay = 0, className = "" }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    const el = ref.current;
    if (!el || reduce) return;
    el.style.minWidth = `${el.getBoundingClientRect().width}px`;
    el.style.display = "inline-block";
    el.textContent = "";
    let timer;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting || el.dataset.typed) return;
        el.dataset.typed = "1";
        io.disconnect();
        let i = 0;
        const tick = () => {
          el.textContent = text.slice(0, ++i);
          if (i < text.length) timer = setTimeout(tick, 24 + Math.random() * 46);
          else el.classList.add("is-done");
        };
        timer = setTimeout(tick, delay);
      },
      { threshold: 0.6 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearTimeout(timer);
    };
  }, [text, delay, reduce]);
  return (
    <span ref={ref} className={`kx-type ${className} ${reduce ? "is-done" : ""}`}>
      {text}
    </span>
  );
}

/* Oscilloscope trace for CONNECT: same point count per frame so framer can
   tween the `d` attribute between them. */
const WAVE_PTS = 48;
const wavePath = (phase, amp) => {
  let d = "";
  for (let i = 0; i <= WAVE_PTS; i++) {
    const t = i / WAVE_PTS;
    const env = Math.sin(t * Math.PI); // taper to the edges
    const y =
      100 +
      Math.sin(i * 0.55 + phase) * amp * env +
      Math.sin(i * 1.7 + phase * 2) * amp * 0.25 * env;
    d += `${i ? "L" : "M"}${(t * 1200).toFixed(1)},${y.toFixed(1)}`;
  }
  return d;
};
const WAVES = [wavePath(0, 42), wavePath(2.1, 30), wavePath(4.2, 46), wavePath(0, 42)];

/* Chapter-intro band: a different reveal shape per chapter.
   [initial, animate, exit] clip-paths. */
const BANNER_CLIPS = [
  null,
  ["inset(0 100% 0 0)", "inset(0 0% 0 0)", "inset(0 0 0 100%)"], // 01 wipe
  ["circle(0% at 50% 50%)", "circle(75% at 50% 50%)", "circle(0% at 50% 50%)"], // 02 iris
  [
    "polygon(0 0, 0 0, -20% 100%, -20% 100%)",
    "polygon(0 0, 120% 0, 100% 100%, -20% 100%)",
    "polygon(120% 0, 120% 0, 100% 100%, 100% 100%)",
  ], // 03 diagonal slice
  ["inset(50% 0 50% 0)", "inset(0 0 0 0)", "inset(50% 0 50% 0)"], // 04 blinds
];

/* One ref feeds both framer's useScroll and the parent's chapter observer. */
const useSectionRef = (sectionRefs, idx) => {
  const ref = useRef(null);
  const setRef = useCallback(
    (el) => {
      ref.current = el;
      sectionRefs.current[idx] = el;
    },
    [sectionRefs, idx],
  );
  return [ref, setRef];
};

/* ---- 00 HOME: viewfinder HUD + kinetic type ---------------------------- */
/* Corner brackets draw themselves, a targeting reticle locks onto the device
   and lags the cursor, readouts tick. The name is giant outlined type whose
   letters flip in on load and scatter apart (alternating up/down) on scroll. */
const HUD_CORNERS = ["tl", "tr", "br", "bl"];
const BRACKET_D = {
  tl: "M2 46 V2 H46",
  tr: "M2 2 H46 V46",
  br: "M46 2 V46 H2",
  bl: "M46 46 H2 V2",
};

/* T+hh:mm:ss since mount, written straight to the DOM (no re-renders). */
const Clock = () => {
  const ref = useRef(null);
  useEffect(() => {
    const t0 = Date.now();
    const pad = (n) => String(n).padStart(2, "0");
    const id = setInterval(() => {
      if (!ref.current) return;
      const s = Math.floor((Date.now() - t0) / 1000);
      ref.current.textContent = `T+${pad(Math.floor(s / 3600))}:${pad(
        Math.floor((s % 3600) / 60),
      )}:${pad(s % 60)}`;
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return <span ref={ref}>T+00:00:00</span>;
};

/* Outer span: load-time 3D flip. Inner span: scroll-linked scatter. */
const KineticLetter = ({ p, i, ch, delay }) => {
  const reduce = useReducedMotion();
  const dir = i % 2 ? -1 : 1;
  const y = useTransform(p, [0, 0.6], [0, dir * (50 + i * 9)]);
  const rotate = useTransform(p, [0, 0.6], [0, dir * (3 + i)]);
  const opacity = useTransform(p, [0.15, 0.6], [1, 0]);
  return (
    <motion.span
      className="kx-kl"
      initial={reduce ? false : { rotateX: 90, y: 50, opacity: 0 }}
      animate={{ rotateX: 0, y: 0, opacity: 1 }}
      transition={{ delay, duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
      aria-hidden
    >
      <motion.span className="kx-kl-in" style={reduce ? undefined : { y, rotate, opacity }}>
        {ch}
      </motion.span>
    </motion.span>
  );
};

const HomeChapter = ({ sectionRefs, goTo, progressPct }) => {
  const [ref, setRef] = useSectionRef(sectionRefs, 0);
  const reduce = useReducedMotion();
  const { scrollYProgress: p } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const topY = useTransform(p, [0, 0.3], [0, -30]);
  const topO = useTransform(p, [0, 0.3], [1, 0]);
  const footY = useTransform(p, [0, 0.45], [0, 70]);
  const footO = useTransform(p, [0.05, 0.45], [1, 0]);
  const retScale = useTransform(p, [0, 0.5], [1, 1.7]);
  const retO = useTransform(p, [0, 0.45], [1, 0]);
  const spread = useTransform(p, [0, 0.4], [0, 70]);
  const spreadNeg = useTransform(spread, (v) => -v);
  const bracketO = useTransform(p, [0, 0.4], [1, 0]);

  /* Reticle lags the cursor like a HUD on a gimbal. */
  const mx = useSpring(0, { stiffness: 60, damping: 18, mass: 0.8 });
  const my = useSpring(0, { stiffness: 60, damping: 18, mass: 0.8 });
  useEffect(() => {
    if (reduce) return;
    const onMove = (e) => {
      mx.set((e.clientX / window.innerWidth - 0.5) * 40);
      my.set((e.clientY / window.innerHeight - 0.5) * 26);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my, reduce]);

  const letters = (word, offset) =>
    Array.from(word).map((ch, i) => (
      <KineticLetter key={i} p={p} i={offset + i} ch={ch} delay={1.1 + (offset + i) * 0.045} />
    ));

  return (
    <section id="home" data-idx={0} ref={setRef} className="kx-chapter kx-home">
      <div className="kx-hud" aria-hidden>
        {HUD_CORNERS.map((c, i) => (
          <motion.svg
            key={c}
            className={`kx-bracket kx-bracket--${c}`}
            viewBox="0 0 48 48"
            style={
              reduce
                ? undefined
                : {
                    x: c === "tl" || c === "bl" ? spreadNeg : spread,
                    y: c === "tl" || c === "tr" ? spreadNeg : spread,
                    opacity: bracketO,
                  }
            }
          >
            <motion.path
              d={BRACKET_D[c]}
              pathLength={1}
              initial={reduce ? false : { pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.9 + i * 0.08, duration: 0.8, ease: "easeOut" }}
            />
          </motion.svg>
        ))}
        <motion.div
          className="kx-reticle"
          initial={reduce ? false : { scale: 1.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1, duration: 1, ease: [0.2, 0.7, 0.2, 1] }}
        >
          <motion.div
            className="kx-reticle-in"
            style={reduce ? undefined : { scale: retScale, opacity: retO, x: mx, y: my }}
          >
            <i className="kx-reticle-ring" />
            <i className="kx-reticle-ticks" />
            <i className="kx-reticle-cross" />
            <span className="kx-reticle-label kx-reticle-label--tl">TARGET · DEVICE_01</span>
            <span className="kx-reticle-label kx-reticle-label--br">
              <i className="kx-rec" />
              LOCKED
            </span>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        className="kx-home-top"
        style={reduce ? undefined : { y: topY, opacity: topO }}
      >
        <div className="kx-home-eyebrow">
          <span className="kx-eyebrow-dot" /> PORTFOLIO
        </div>
        <div className="kx-readouts">
          <span className="kx-readout">
            <i className="kx-rec" />
            REC <Clock />
          </span>
          <span className="kx-readout">
            SCROLL <motion.b>{progressPct}</motion.b>
          </span>
          <span className="kx-readout kx-readout--sys">
            <TypeLine text="DEVICE ONLINE // INTERACTIVE" delay={1400} />
          </span>
        </div>
      </motion.div>

      <div className="kx-home-bottom">
        <h1 className="kx-name-k" aria-label="Dexter Chong">
          <span className="kx-name-k-word">{letters("DEXTER", 0)}</span>
          <span className="kx-name-k-word kx-name-k-word--accent">{letters("Chong", 6)}</span>
        </h1>
        <motion.div
          className="kx-home-rule"
          initial={reduce ? false : { scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.5, duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
          aria-hidden
        />
        <motion.div
          className="kx-home-foot"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.8 }}
        >
          <motion.div
            className="kx-home-foot-in"
            style={reduce ? undefined : { y: footY, opacity: footO }}
          >
            <p className="kx-home-tag">
              Frontend engineer crafting cinematic interfaces with{" "}
              <em className="kx-em">React</em>, Three.js, and an obsession with
              motion.
            </p>
            <div className="kx-meta-grid">
              <div className="kx-meta-cell">
                <span className="kx-meta-key">ROLE</span>
                <ScrambleText className="kx-meta-val" text="Software Engineer" />
              </div>
              <div className="kx-meta-cell">
                <span className="kx-meta-key">BUILD</span>
                <ScrambleText className="kx-meta-val" text="v3.0" />
              </div>
              <div className="kx-meta-cell">
                <span className="kx-meta-key">LOC</span>
                <ScrambleText className="kx-meta-val" text="Malaysia · GMT+8" />
              </div>
              <div className="kx-meta-cell">
                <span className="kx-meta-key">STATUS</span>
                <span className="kx-meta-val">
                  <span className="kx-status-dot" /> Open to chat
                </span>
              </div>
            </div>
            <div className="kx-cta-row">
              <button
                type="button"
                className="kx-cta-primary kx-magnet"
                onClick={() => goTo("contact")}
                data-cursor="CONNECT"
              >
                Connect
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="kx-cta-ghost kx-magnet"
                onClick={() => goTo("about")}
                data-cursor="MORE"
              >
                More about me
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---- 01 PROFILE: dossier spread ---------------------------------------- */
/* A data spine draws down the left and lights a node per block. A holo ID
   card flips in (shimmer, chip, spinning monogram ring, barcode drawn bar by
   bar from one CSS var, a VERIFIED stamp that slams in). The bio is revealed
   by a hologram scan: a stroked blueprint copy sits under the real copy and
   a glowing scanline (--scan 0..100) materialises it. Exit: CRT power-off. */
const BARS = Array.from({ length: 30 }, (_, i) => 1 + ((i * 7) % 3));

const ProfileChapter = ({ sectionRefs }) => {
  const [ref, setRef] = useSectionRef(sectionRefs, 1);
  const reduce = useReducedMotion();
  const p = useChapterProgress(ref);
  const x = useTransform(p, [0.04, 0.3], [-48, 0]);
  const scaleY = useTransform(p, [EXIT[0], EXIT[1]], [1, 0.004]);
  const opacity = useTransform(p, [EXIT[0], 0.9, EXIT[1]], [1, 1, 0]);
  const spine = useTransform(p, [0.08, 0.56], [0, 1]);
  const node0 = useTransform(p, [0.1, 0.14], [0, 1]);
  const node1 = useTransform(p, [0.26, 0.3], [0, 1]);
  const node2 = useTransform(p, [0.44, 0.48], [0, 1]);
  const cardRot = useTransform(p, [0.06, 0.3], [-100, 0]);
  const cardO = useTransform(p, [0.06, 0.14], [0, 1]);
  const bars = useTransform(p, [0.18, 0.4], [0, 1]);
  const stampS = useTransform(p, [0.5, 0.56], [3, 1]);
  const stampO = useTransform(p, [0.5, 0.56], [0, 1]);
  const scan = useTransform(p, [0.24, 0.54], [0, 100]);
  const scanO = useTransform(p, [0.24, 0.27, 0.52, 0.55], [0, 1, 1, 0]);
  const stripY = useTransform(p, [0.4, 0.58], [30, 0]);
  const stripO = useTransform(p, [0.4, 0.58], [0, 1]);

  const bio = (
    <>
      <p className="kx-chapter-lead">
        Frontend engineer focused on{" "}
        <em className="kx-em">cinematic, ultra-smooth</em> interfaces. React,
        Angular, TypeScript, and a serious obsession with motion.
      </p>
      <div className="kx-chapter-body">
        <p>
          I'm at the start of my career, but I bring fresh perspective, modern
          tooling, and an unwavering commitment to craft. I write clean,
          maintainable code and spend a lot of time obsessing over the small
          details — the easing curve, the focus ring, the loading state.
        </p>
      </div>
    </>
  );

  return (
    <section id="about" data-idx={1} ref={setRef} className="kx-chapter kx-about">
      <motion.div
        className="kx-dossier kx-side-content kx-side-content--left"
        style={reduce ? undefined : { "--scan": scan, "--bars": bars, x, scaleY, opacity }}
      >
        <motion.i
          className="kx-spine"
          style={reduce ? undefined : { scaleY: spine }}
          aria-hidden
        />

        <div className="kx-chapter-header">
          <span className="kx-chapter-num">01</span>
          <StaggeredHeading text="Profile." className="kx-chapter-title" delay={0.15} />
        </div>

        <motion.div
          className="kx-idcard-wrap"
          style={
            reduce
              ? undefined
              : { rotateY: cardRot, opacity: cardO, transformOrigin: "left center" }
          }
        >
          <motion.i
            className="kx-spine-node"
            style={reduce ? undefined : { opacity: node0 }}
            aria-hidden
          />
          <TiltCard className="kx-idcard" intensity={9}>
            <span className="kx-idcard-holo" aria-hidden />
            <span className="kx-idcard-shimmer" aria-hidden />
            <div className="kx-idcard-head">
              <span className="kx-idcard-chip" aria-hidden />
              <TypeLine text="ID // DEXTER-CHONG-JX" />
              <span className="kx-idcard-ver">v3.0</span>
            </div>
            <div className="kx-idcard-body">
              <div className="kx-idcard-mono" aria-hidden>
                <span>DC</span>
              </div>
              <dl className="kx-idcard-lines">
                <dt>NAME</dt>
                <dd>Dexter Chong Jun Xiang</dd>
                <dt>ROLE</dt>
                <dd>Frontend Engineer</dd>
                <dt>BASE</dt>
                <dd>Malaysia · GMT+8</dd>
              </dl>
            </div>
            <div className="kx-idcard-foot">
              <span className="kx-barcode" aria-hidden>
                {BARS.map((w, i) => (
                  <i key={i} style={{ "--i": i, "--w": w }} />
                ))}
              </span>
              <span className="kx-idcard-id">0105 · 2026</span>
            </div>
            <motion.span
              className="kx-idcard-stamp"
              style={
                reduce ? { rotate: -12 } : { scale: stampS, opacity: stampO, rotate: -12 }
              }
              aria-hidden
            >
              VERIFIED
            </motion.span>
          </TiltCard>
        </motion.div>

        <div className="kx-dossier-stack">
          <motion.i
            className="kx-spine-node"
            style={reduce ? undefined : { opacity: node1 }}
            aria-hidden
          />
          <div className="kx-dossier-layer kx-dossier-skel" aria-hidden inert>
            {bio}
          </div>
          <div className="kx-dossier-layer kx-dossier-real">{bio}</div>
          <motion.div
            className="kx-dossier-scanline"
            style={reduce ? undefined : { opacity: scanO }}
            aria-hidden
          />
        </div>

        <motion.div
          className="kx-stat-strip"
          style={reduce ? undefined : { y: stripY, opacity: stripO }}
        >
          <motion.i
            className="kx-spine-node"
            style={reduce ? undefined : { opacity: node2 }}
            aria-hidden
          />
          <div className="kx-stat-big">
            <span className="kx-stat-big-num">
              <Counter to={4} suffix="+" />
            </span>
            <span className="kx-stat-big-label">Frontend stacks</span>
          </div>
          <div className="kx-stat-big">
            <span className="kx-stat-big-num">
              <em className="kx-em">∞</em>
            </span>
            <span className="kx-stat-big-label">Curiosity</span>
          </div>
          <div className="kx-stat-big">
            <span className="kx-stat-big-num">
              <ScrambleText text="READY" />
            </span>
            <span className="kx-stat-big-label">For projects</span>
          </div>
        </motion.div>

        <div className="kx-tip-row">
          <span className="kx-tip-key">TIP</span>
          Pull the lever on the device to spin all reels.
        </div>
      </motion.div>
    </section>
  );
};

/* ---- 02 SKILLS: orbital system ----------------------------------------- */
/* Skills are badges parked on two dashed orbit rings that spin in opposite
   directions (CSS); each badge un-spins so it stays upright. The ring-arc
   around a badge is its level and draws in with scroll. The whole system
   boots up (scale + rotate) and exits by flying through the camera. */
const SkillNode = ({ p, i, skill }) => {
  const reduce = useReducedMotion();
  const ring = i % 2; // alternate rings, 3 badges each
  const angle = Math.floor(i / 2) * 120 + ring * 60 - 90;
  const s = 0.2 + i * 0.03;
  const arc = useTransform(p, [s, s + 0.26], [0, skill.level / 100]);
  const pop = useTransform(p, [s - 0.1, s + 0.1], [0, 1]);
  return (
    <div className="kx-node" style={{ "--a": `${angle}deg` }}>
      <div className="kx-node-unspin">
        <motion.div
          className="kx-node-inner"
          style={reduce ? { rotate: -angle } : { rotate: -angle, scale: pop, opacity: pop }}
          data-cursor={`${skill.level}%`}
        >
          <svg className="kx-node-arc" viewBox="0 0 100 100" aria-hidden>
            <circle className="kx-node-track" cx="50" cy="50" r="46" />
            <motion.circle
              className="kx-node-fill"
              cx="50"
              cy="50"
              r="46"
              pathLength={1}
              style={reduce ? { pathLength: skill.level / 100 } : { pathLength: arc }}
            />
          </svg>
          <skill.icon className="kx-node-icon" />
          <span className="kx-node-name">{skill.name}</span>
          <span className="kx-node-level">{skill.level}</span>
        </motion.div>
      </div>
    </div>
  );
};

const SkillsChapter = ({ sectionRefs }) => {
  const [ref, setRef] = useSectionRef(sectionRefs, 2);
  const reduce = useReducedMotion();
  const p = useChapterProgress(ref);
  const headY = useInOut(p, [0.06, 0.3], -24, 0, -24);
  const headO = useInOut(p, [0.06, 0.3], 0, 1, 0);
  const sysScale = useInOut(p, [0.06, 0.4], 0.25, 1, 1.5);
  const sysRot = useTransform(p, [0.06, 0.4], [-140, 0]);
  const sysO = useInOut(p, [0.06, 0.3], 0, 1, 0);
  const sysBlur = useTransform(p, [0.06, 0.34], ["blur(10px)", "blur(0px)"]);
  const rings = [0, 1].map((r) =>
    SKILLS.map((skill, i) =>
      i % 2 === r ? <SkillNode key={skill.name} p={p} i={i} skill={skill} /> : null,
    ),
  );

  return (
    <section id="skills" data-idx={2} ref={setRef} className="kx-chapter kx-skills">
      <motion.div
        className="kx-chapter-header kx-chapter-header--center"
        style={reduce ? undefined : { y: headY, opacity: headO }}
      >
        <span className="kx-chapter-num">02</span>
        <StaggeredHeading text="Skill Core." className="kx-chapter-title" delay={0.15} />
      </motion.div>

      <motion.p
        className="kx-chapter-lead kx-center"
        style={reduce ? undefined : { y: headY, opacity: headO }}
      >
        Six modules in orbit around the core. Hover a badge for its level.
      </motion.p>

      <motion.div
        className="kx-orbit"
        style={
          reduce ? undefined : { scale: sysScale, rotate: sysRot, opacity: sysO, filter: sysBlur }
        }
      >
        <div className="kx-orbit-core" aria-hidden>
          <Zap className="kx-orbit-core-icon" />
          <span>CORE</span>
        </div>
        <div className="kx-orbit-ring kx-orbit-ring--a">{rings[0]}</div>
        <div className="kx-orbit-ring kx-orbit-ring--b">{rings[1]}</div>
      </motion.div>

      <div className="kx-skills-tip">
        <span className="kx-tip-key">CLICK</span> a reel on the device to spin
        it — each reel is one of the skills.
      </div>
    </section>
  );
};

/* ---- 03 SERVICES: pinned horizontal film reel -------------------------- */
/* The section is 300vh tall with a sticky 100vh stage; vertical scroll is
   turned into a horizontal slide of giant ticket panels. The panel nearest
   centre is full-size, the others recede. */
const ReelPanel = ({ p, i, n, service }) => {
  const reduce = useReducedMotion();
  const dist = useTransform(p, (v) => Math.min(1, Math.abs(i - v * (n - 1))));
  const scale = useTransform(dist, [0, 1], [1, 0.88]);
  const opacity = useTransform(dist, [0, 1], [1, 0.4]);
  const [first, ...rest] = service.title.split(" ");
  return (
    <motion.article className="kx-reel-panel" style={reduce ? undefined : { scale, opacity }}>
      <span className="kx-reel-no" aria-hidden>
        {service.no}
      </span>
      <div className="kx-reel-body">
        <span className="kx-reel-tag">
          SERVICE {service.no} / 0{n}
        </span>
        <h3 className="kx-reel-title">
          {first} <em>{rest.join(" ")}</em>
        </h3>
        <p className="kx-reel-desc">{service.description}</p>
      </div>
      <div className="kx-reel-foot">
        <span className="kx-reel-perf" aria-hidden />
        <ChevronRight className="kx-reel-arrow" />
      </div>
    </motion.article>
  );
};

const ServicesChapter = ({ sectionRefs }) => {
  const [ref, setRef] = useSectionRef(sectionRefs, 3);
  const reduce = useReducedMotion();
  const { scrollYProgress: p } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const n = SERVICES.length;
  /* Step width lives in CSS (--kx-reel-step) so mobile can widen panels. */
  const x = useTransform(p, (v) => `calc(${(-v * (n - 1)).toFixed(4)} * var(--kx-reel-step))`);
  const rail = useTransform(p, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="services"
      data-idx={3}
      ref={setRef}
      className="kx-chapter kx-services kx-reel"
    >
      <div className="kx-reel-sticky">
        <div className="kx-reel-head">
          <div className="kx-chapter-header">
            <span className="kx-chapter-num">03</span>
            <StaggeredHeading text="Services." className="kx-chapter-title" delay={0.15} />
          </div>
          <p className="kx-chapter-lead">
            <em className="kx-em">Cinematic</em>, accessible, performant — from
            concept to production. Keep scrolling to advance the reel.
          </p>
        </div>

        <motion.div className="kx-reel-track" style={reduce ? undefined : { x }}>
          {SERVICES.map((s, i) => (
            <ReelPanel key={s.title} p={p} i={i} n={n} service={s} />
          ))}
        </motion.div>

        <div className="kx-reel-rail" aria-hidden>
          <motion.div
            className="kx-reel-rail-fill"
            style={reduce ? undefined : { width: rail }}
          />
          {SERVICES.map((s) => (
            <span key={s.no} className="kx-reel-rail-mark">
              {s.no}
            </span>
          ))}
        </div>

        <div className="kx-tip-row kx-reel-tip">
          <span className="kx-tip-key">TIP</span>
          The BET button on the device changes your bet amount.
        </div>
      </div>
    </section>
  );
};

/* ---- 04 CONNECT: transmission ------------------------------------------ */
/* An oscilloscope trace draws in with scroll and keeps breathing, a
   circular-text dial spins around the mail CTA, and the contact "channels"
   wipe in like a terminal with live signal bars. (Sonar pings live in the
   Hero3D background shader.) */
const Channel = ({ p, i, icon: Icon, label, value, sub, href }) => {
  const reduce = useReducedMotion();
  const s = 0.16 + i * 0.05;
  const clipPath = useTransform(
    p,
    [s, s + 0.22],
    ["inset(0% 100% 0% 0%)", "inset(0% 0% 0% 0%)"],
  );
  const Tag = href ? "a" : "div";
  return (
    <motion.li className="kx-channel" style={reduce ? undefined : { clipPath }}>
      <Tag className="kx-channel-link" href={href}>
        <span className="kx-channel-bars" aria-hidden>
          <i />
          <i />
          <i />
          <i />
        </span>
        <Icon className="kx-channel-icon" />
        <span className="kx-channel-text">
          <span className="kx-channel-label">{label}</span>
          <span className="kx-channel-value">{value}</span>
          <span className="kx-channel-sub">{sub}</span>
        </span>
        <span className="kx-channel-status">LIVE</span>
      </Tag>
    </motion.li>
  );
};

const ConnectChapter = ({ sectionRefs }) => {
  const [ref, setRef] = useSectionRef(sectionRefs, 4);
  const reduce = useReducedMotion();
  const p = useChapterProgress(ref);
  /* Last chapter: p tops out near 0.5 (page ends), so everything lands early. */
  const draw = useTransform(p, [0.06, 0.42], [0, 1]);
  const y = useTransform(p, [0.04, 0.32], [120, 0]);
  const opacity = useTransform(p, [0.04, 0.26], [0, 1]);
  const sayScale = useTransform(p, [0.08, 0.36], [1.25, 1]);
  const dialScale = useTransform(p, [0.2, 0.42], [0, 1]);

  return (
    <section id="contact" data-idx={4} ref={setRef} className="kx-chapter kx-contact">
      <svg className="kx-wave" viewBox="0 0 1200 200" preserveAspectRatio="none" aria-hidden>
        <motion.path
          d={WAVES[0]}
          pathLength={1}
          animate={reduce ? undefined : { d: WAVES }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          style={reduce ? undefined : { pathLength: draw }}
        />
      </svg>

      <motion.div
        className="kx-contact-stage"
        style={reduce ? undefined : { y, opacity }}
      >
        <div className="kx-contact-intro">
          <div className="kx-chapter-header">
            <span className="kx-chapter-num">04</span>
            <StaggeredHeading text="Connect." className="kx-chapter-title" delay={0.15} />
          </div>
          <motion.p
            className="kx-say"
            style={reduce ? undefined : { scale: sayScale, transformOrigin: "left bottom" }}
          >
            Say <em className="kx-em">hello.</em>
          </motion.p>
          <motion.a
            className="kx-dial kx-magnet"
            href="mailto:dexterchong105@gmail.com"
            data-cursor="EMAIL"
            aria-label="Email Dexter"
            style={reduce ? undefined : { scale: dialScale }}
          >
            <svg viewBox="0 0 160 160" aria-hidden>
              <defs>
                <path
                  id="kx-dial-path"
                  d="M80,80 m-58,0 a58,58 0 1,1 116,0 a58,58 0 1,1 -116,0"
                />
              </defs>
              <text textLength="362" lengthAdjust="spacing">
                <textPath href="#kx-dial-path">
                  OPEN TO WORK · SAY HELLO · OPEN TO WORK · SAY HELLO ·
                </textPath>
              </text>
            </svg>
            <span className="kx-dial-arrow">
              <ArrowUpRight />
            </span>
          </motion.a>
        </div>

        <ul className="kx-channels">
          <Channel
            p={p}
            i={0}
            icon={Mail}
            label="EMAIL"
            value="dexterchong105@gmail.com"
            sub="I'll respond as soon as I can"
            href="mailto:dexterchong105@gmail.com"
          />
          <Channel
            p={p}
            i={1}
            icon={Phone}
            label="PHONE"
            value="+60 16-848-9983"
            sub="Messages preferred"
          />
          <Channel
            p={p}
            i={2}
            icon={MapPin}
            label="LOCATION"
            value="Malaysia"
            sub="Working in GMT+8"
          />
        </ul>
      </motion.div>
    </section>
  );
};

const Portfolio = () => {
  const rootRef = useRef(null);
  const sectionRefs = useRef([]);
  const heroProgressRef = useRef(0);
  const chapterRef = useRef(0);
  const hudTimerRef = useRef(0);

  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  const cursorLabelRef = useRef(null);
  const spotRef = useRef(null);
  const hudChRef = useRef(null);

  const reduceMotion = useReducedMotion();

  const [activeIdx, setActiveIdx] = useState(0);
  const [hudHint, setHudHint] = useState(null);
  const [curtainGone, setCurtainGone] = useState(false);

  /* Cinematic chapter intro: a slim band that flashes across the viewport
     when you cross into a new chapter (skipped on chapter 0 since the hero
     already owns the screen). */
  const [bannerKey, setBannerKey] = useState(null);
  const bannerTimerRef = useRef(0);
  const prevChapterRef = useRef(0);

  useEffect(() => {
    if (activeIdx === prevChapterRef.current) return;
    prevChapterRef.current = activeIdx;
    if (reduceMotion || activeIdx === 0) {
      setBannerKey(null);
      return;
    }
    setBannerKey({ idx: activeIdx, t: Date.now() });
    clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setBannerKey(null), 1700);
  }, [activeIdx, reduceMotion]);

  useEffect(() => () => clearTimeout(bannerTimerRef.current), []);

  const bannerSection = bannerKey ? SECTIONS[bannerKey.idx] : null;

  const { scrollYProgress } = useScroll({
    target: rootRef,
    offset: ["start start", "end end"],
  });

  // Narrative progress for the 3D device (rotate / explode / reassemble):
  // piecewise-linear over the scroll positions at which each chapter sits
  // centred in the viewport, so chapter i centred => i/4 regardless of how
  // tall it is (Services is a 300vh pinned reel). With Hero3D's phases this
  // means: exploding through Skills, fully apart in the Skills→Services gap,
  // rebuilding during the first half of the reel.
  useEffect(() => {
    const update = () => {
      const els = sectionRefs.current.filter(Boolean);
      if (els.length < 2) return;
      const y = window.scrollY;
      const vh = window.innerHeight;
      const keys = els.map((el) => {
        const r = el.getBoundingClientRect();
        return Math.max(0, r.top + y + r.height / 2 - vh / 2);
      });
      let v = 1;
      if (y <= keys[0]) v = 0;
      else {
        for (let i = 0; i < keys.length - 1; i++) {
          if (y < keys[i + 1]) {
            v = (i + (y - keys[i]) / (keys[i + 1] - keys[i])) / (keys.length - 1);
            break;
          }
        }
      }
      heroProgressRef.current = Math.max(0, Math.min(1, v));
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // Transform values used by HUD progress bar etc.
  const progressPct = useTransform(scrollYProgress, (v) => `${Math.round(v * 100)}%`);
  const progressWidth = useTransform(scrollYProgress, (v) => `${v * 100}%`);

  // Keep chapterRef in sync so Hero3D (reads .current each frame) updates
  useEffect(() => {
    chapterRef.current = activeIdx;
  }, [activeIdx]);

  // Curtain: lift after the page hits its first paint
  useEffect(() => {
    const t = setTimeout(() => setCurtainGone(true), 1100);
    return () => clearTimeout(t);
  }, []);

  // Custom cursor + mouse spot — DOM-mutating loop, runs once
  useEffect(() => {
    if (reduceMotion) return;
    const cursor = cursorRef.current;
    const dot = cursorDotRef.current;
    const spot = spotRef.current;
    if (!cursor || !dot || !spot) return;

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let cx = mx;
    let cy = my;
    let dx = mx;
    let dy = my;
    let raf = 0;

    const onMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
      spot.style.setProperty("--mx", `${mx}px`);
      spot.style.setProperty("--my", `${my}px`);
    };
    const tick = () => {
      cx += (mx - cx) * 0.18;
      cy += (my - cy) * 0.18;
      dx += (mx - dx) * 0.45;
      dy += (my - dy) * 0.45;
      cursor.style.transform = `translate3d(${cx}px,${cy}px,0) translate(-50%,-50%)`;
      dot.style.transform = `translate3d(${dx}px,${dy}px,0) translate(-50%,-50%)`;
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
    };
  }, [reduceMotion]);

  // Cursor hover state on interactive elements
  useEffect(() => {
    if (reduceMotion) return;
    const cursor = cursorRef.current;
    const label = cursorLabelRef.current;
    if (!cursor || !label) return;
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll(
      "a, button, .kx-magnet, .kx-stat, .kx-node-inner, .kx-reel-panel, .kx-channel, .kx-topbar-link, .kx-chapter-dot",
    );
    const cleanup = [];
    targets.forEach((el) => {
      const enter = () => {
        cursor.classList.add("is-hover");
        label.textContent = el.getAttribute("data-cursor") || "";
      };
      const leave = () => {
        cursor.classList.remove("is-hover");
        label.textContent = "";
      };
      el.addEventListener("mouseenter", enter);
      el.addEventListener("mouseleave", leave);
      cleanup.push(() => {
        el.removeEventListener("mouseenter", enter);
        el.removeEventListener("mouseleave", leave);
      });
    });
    return () => cleanup.forEach((fn) => fn());
  }, [reduceMotion]);

  // Scramble + glitch the HUD chapter counter on chapter change
  useEffect(() => {
    const el = hudChRef.current;
    if (!el) return;
    const txt = `${String(activeIdx + 1).padStart(2, "0")} / 05`;
    el.dataset.text = txt;
    if (reduceMotion) {
      el.textContent = txt;
      return;
    }
    el.classList.add("kx-glitch-burst");
    const t = setTimeout(() => el.classList.remove("kx-glitch-burst"), 380);
    runScramble(el, txt, 600);
    return () => clearTimeout(t);
  }, [activeIdx, reduceMotion]);

  // Magnet pull + per-card spotlight
  useMagnetAndSpotEffects(rootRef, reduceMotion);

  // Track which section's center is closest to viewport center -> active chapter
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let best = null;
        let bestRatio = -Infinity;
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > bestRatio) {
            best = e;
            bestRatio = e.intersectionRatio;
          }
        });
        if (best) {
          const idx = Number(best.target.dataset.idx);
          if (!Number.isNaN(idx)) setActiveIdx(idx);
        }
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handlePartActivate = useCallback((info) => {
    setHudHint(info);
    clearTimeout(hudTimerRef.current);
    // Big wins/jackpot stay visible longer for satisfaction
    const duration =
      info.kind === "jackpot" ? 3200
      : info.kind === "win" ? 2400
      : info.kind === "lose" ? 1800
      : info.kind === "spin" ? 1200
      : 1500;
    hudTimerRef.current = setTimeout(() => setHudHint(null), duration);
  }, []);

  useEffect(() => () => clearTimeout(hudTimerRef.current), []);

  const goTo = useCallback((id) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div
      ref={rootRef}
      className="kx-root premium-root"
      data-chapter={activeIdx}
      style={{
        "--chapter-accent": SECTIONS[activeIdx].accent,
        "--chapter-accent-rgb": SECTIONS[activeIdx].accentRgb,
      }}
    >
      {/* Curtain (boot screen) */}
      <div className={`kx-curtain ${curtainGone ? "gone" : ""}`} aria-hidden>
        <div className="kx-curtain-num">v3.0 / 2026</div>
        <div className="kx-curtain-inner">
          <span>Booting</span>
          <span>·</span>
          <span>KEDIGX</span>
          <span>·</span>
          <span>Portfolio</span>
        </div>
        <div className="kx-curtain-bar" />
      </div>

      {/* Mouse spotlight + custom cursor (hover devices only) */}
      <div className="kx-spot" ref={spotRef} aria-hidden />
      <div className="kx-cursor" ref={cursorRef} aria-hidden>
        <span className="kx-cursor-label" ref={cursorLabelRef} />
      </div>
      <div className="kx-cursor-dot" ref={cursorDotRef} aria-hidden />

      {/* Sticky 3D layer - the device follows you the whole way */}
      <div className="hero3d-stage">
        <Hero3D
          progressRef={heroProgressRef}
          chapterRef={chapterRef}
          onPartActivate={handlePartActivate}
        />
        <div className="hero3d-vignette" />
      </div>

      {/* Top brand bar */}
      <header className="kx-topbar">
        <button
          type="button"
          className="kx-topbar-brand kx-magnet"
          onClick={() => goTo("home")}
          data-cursor="HOME"
        >
          <span className="kx-brand-text">
            KEDI<span>GX</span>
          </span>
        </button>
        <nav className="kx-topbar-nav kx-nav-pill">
          {SECTIONS.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goTo(s.id)}
              className={`kx-topbar-link ${i === activeIdx ? "active" : ""}`}
            >
              <span>{s.label}</span>
            </button>
          ))}
        </nav>
        <div className="kx-topbar-meta kx-hud-ch">
          <span>CH</span>
          <span
            ref={hudChRef}
            className="kx-glitch"
            data-text={`${String(activeIdx + 1).padStart(2, "0")} / 05`}
          >
            {String(activeIdx + 1).padStart(2, "0")} / 05
          </span>
        </div>
      </header>

      {/* Right-side chapter dots */}
      <nav className="kx-chapter-nav" aria-label="Chapters">
        {SECTIONS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => goTo(s.id)}
            className={`kx-chapter-dot ${i === activeIdx ? "active" : ""}`}
            aria-label={`Go to ${s.label}`}
          >
            <span className="kx-chapter-dot-label">{s.label}</span>
            <span className="kx-chapter-dot-mark" />
          </button>
        ))}
      </nav>

      {/* Bottom progress rail */}
      <div className="kx-progress-rail" aria-hidden>
        <motion.div className="kx-progress-fill" style={{ width: progressWidth }} />
        <motion.div className="kx-progress-pct">{progressPct}</motion.div>
      </div>

      {/* Cinematic chapter intro band — clip-path reveal + letter stagger */}
      <AnimatePresence>
        {bannerKey && bannerSection && (
          <motion.div
            key={bannerKey.t}
            className="kx-scene-banner"
            style={{
              "--banner-accent": bannerSection.accent,
              "--banner-accent-rgb": bannerSection.accentRgb,
            }}
            initial={{ clipPath: BANNER_CLIPS[bannerKey.idx][0] }}
            animate={{ clipPath: BANNER_CLIPS[bannerKey.idx][1] }}
            exit={{ clipPath: BANNER_CLIPS[bannerKey.idx][2] }}
            transition={{ duration: 0.65, ease: [0.7, 0, 0.3, 1] }}
            aria-hidden
          >
            <div className="kx-scene-banner-grid" />
            <div className="kx-scene-banner-content">
              <motion.span
                className="kx-scene-num"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.45, delay: 0.3, ease: "easeOut" }}
              >
                CH {bannerSection.num}
              </motion.span>
              <motion.span
                className="kx-scene-divider"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.5, delay: 0.4, ease: [0.65, 0, 0.35, 1] }}
              />
              <span className="kx-scene-label" aria-label={bannerSection.label}>
                {splitChars(bannerSection.label).map((c, i) => (
                  <motion.span
                    key={i}
                    className="kx-stagger-char"
                    initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ duration: 0.42, delay: 0.5 + i * 0.045, ease: "easeOut" }}
                    aria-hidden
                  >
                    {c === " " ? " " : c}
                  </motion.span>
                ))}
              </span>
            </div>
            <motion.div
              className="kx-scene-banner-pulse"
              initial={{ opacity: 0, scaleX: 0.7 }}
              animate={{ opacity: [0, 1, 0], scaleX: [0.7, 1, 1.06] }}
              transition={{ duration: 1.1, delay: 0.35, ease: "easeInOut" }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating HUD pill - device interaction feedback */}
      <AnimatePresence>
        {hudHint && (
          <motion.div
            key={`${hudHint.kind}-${hudHint.amount || hudHint.value || hudHint.label || ""}-${Date.now()}`}
            className={`hud-pill hud-pill--${hudHint.kind}`}
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <span className="hud-pill-dot" />
            {hudHint.kind === "spin" && (
              <>SPIN &middot; bet <strong>{hudHint.bet}</strong></>
            )}
            {hudHint.kind === "win" && (
              <>WIN &middot; <strong>+{hudHint.amount}</strong> credits</>
            )}
            {hudHint.kind === "jackpot" && (
              <>JACKPOT &middot; <strong>+{hudHint.amount}</strong> credits!</>
            )}
            {hudHint.kind === "lose" && (
              <>{hudHint.message || "No match — try again"}</>
            )}
            {hudHint.kind === "bet" && (
              <>BET set to <strong>{hudHint.value}</strong></>
            )}
            {hudHint.kind === "credit" && (
              <>+<strong>{hudHint.amount}</strong> credits</>
            )}
            {hudHint.kind === "button" && (
              <>BTN <strong>{hudHint.label}</strong></>
            )}
            {hudHint.kind === "lever" && <>LEVER pulled &middot; spinning</>}
            {hudHint.kind === "reel" && (
              <>REEL {(hudHint.index ?? 0) + 1} &middot; spinning</>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ======================== CHAPTER 0 - HOME / HERO ====================== */}
      <HomeChapter sectionRefs={sectionRefs} goTo={goTo} progressPct={progressPct} />

      {/* Marquee strip between the hero and the chapters */}
      <div className="kx-marquee" aria-hidden>
        <div className="kx-marquee-track">
          <span>
            <i>Frontend</i><b>·</b>Cinematic UI<b>·</b>Motion design<b>·</b>React
            <b>·</b>Angular<b>·</b>TypeScript<b>·</b>Three.js<b>·</b>Tailwind
            <b>·</b>Frontend<b>·</b>Cinematic UI<b>·</b>Motion design<b>·</b>React
            <b>·</b>Angular<b>·</b>TypeScript<b>·</b>Three.js<b>·</b>Tailwind<b>·</b>
          </span>
        </div>
      </div>

      {/* ======================== CHAPTER 1 - PROFILE / ABOUT ================== */}
      <ProfileChapter sectionRefs={sectionRefs} />

      {/* ======================== CHAPTER 2 - SKILL CORE ======================= */}
      <SkillsChapter sectionRefs={sectionRefs} />

      {/* ======================== CHAPTER 3 - SERVICES ========================= */}
      <ServicesChapter sectionRefs={sectionRefs} />

      {/* ======================== CHAPTER 4 - CONNECT ========================== */}
      <ConnectChapter sectionRefs={sectionRefs} />

      <footer className="kx-footer">
        <div>© 2026 Dexter Chong Jun Xiang</div>
        <div className="kx-footer-meta">React · Three.js · v3.0</div>
      </footer>
    </div>
  );
};

export default Portfolio;
